import React, { useRef, useState, useEffect } from 'react';
import AceEditor from 'react-ace';
import axios from 'axios';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-github';
import { getToken } from '../../../utils/TokenUtils';
import { API_URL, ENDPOINTS } from '../../../constants/api';
import '../css/Editor.css';

const SqlEditor = () => {
  const [query, setQuery] = useState('SELECT * FROM `pos_category` WHERE 1;');
  const [result, setResult] = useState(null);
  const [rowsAffected, setRowsAffected] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState(0);
  const [isNestedArray, setIsNestedArray] = useState(false);

  const token = getToken();
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  const executeQuery = async () => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setRowsAffected(null);

      startTimeRef.current = performance.now();
      timerRef.current = setInterval(() => {
        setExecutionTime(
          ((performance.now() - startTimeRef.current) / 1000).toFixed(4)
        );
      }, 50);

      const response = await axios.post(
        `${API_URL}/${ENDPOINTS.SQL}/execute-query`,
        { query },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      clearInterval(timerRef.current);
      setExecutionTime(
        ((performance.now() - startTimeRef.current) / 1000).toFixed(4)
      );

      const { data } = response;

      if (Array.isArray(data) && data.length > 0) {
        const isNested = data.some((item) => Array.isArray(item));
        setIsNestedArray(isNested);
        setResult(data);
      } else if (data.affectedRows !== undefined) {
        setRowsAffected(data.affectedRows);
      } else {
        setResult([]);
      }

      if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      clearInterval(timerRef.current);
      setError(
        err.response?.data?.message || err.message || 'Failed to execute query'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResult(null);
    setError(null);
    setRowsAffected(null);
  };

  const setTemplateQuery = (type) => {
    switch (type) {
      case 'SELECT':
        setQuery('SELECT * FROM `table_name` WHERE 1;');
        break;
      case 'INSERT':
        setQuery(
          "INSERT INTO `table_name` (`column1`, `column2`) VALUES ('value1', 'value2');"
        );
        break;
      case 'UPDATE':
        setQuery(
          "UPDATE `table_name` SET `column1` = 'value1' WHERE `id` = 1;"
        );
        break;
      case 'DELETE':
        setQuery('DELETE FROM `table_name` WHERE `id` = 1;');
        break;
      default:
        break;
    }
  };

  return (
    <div className="pma-sql-editor-container">
      {/* phpMyAdmin SQL Form Styling */}
      <style>{`
        .pma-sql-box {
          border: 1px solid #aaa;
          background: #f8f8f8;
          border-radius: 3px;
          padding: 12px;
          margin-bottom: 15px;
        }
        .pma-sql-legend {
          font-weight: bold;
          font-size: 13px;
          color: #333;
          margin-bottom: 8px;
        }
        .pma-editor-wrapper {
          border: 1px solid #ccc;
          border-radius: 2px;
          background: #fff;
        }
        .pma-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid #e0e0e0;
        }
        .pma-btn {
          background: linear-gradient(to bottom, #f2f2f2, #dcdcdc);
          border: 1px solid #888;
          border-radius: 3px;
          padding: 4px 12px;
          font-size: 12px;
          font-weight: bold;
          color: #222;
          cursor: pointer;
        }
        .pma-btn:hover {
          background: linear-gradient(to bottom, #ffffff, #e0e0e0);
        }
        .pma-btn-primary {
          background: linear-gradient(to bottom, #4d90fe, #4787ed);
          border: 1px solid #3079ed;
          color: #fff;
        }
        .pma-btn-primary:hover {
          background: linear-gradient(to bottom, #357ae8, #2f5bb7);
        }
        .pma-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .pma-quick-buttons {
          display: flex;
          gap: 5px;
        }
        .pma-quick-btn {
          background: #e0e0e0;
          border: 1px solid #b0b0b0;
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 2px;
          cursor: pointer;
        }
        .pma-quick-btn:hover {
          background: #d0d0d0;
        }
        .pma-alert-success {
          background-color: #e2f0d9;
          border: 1px solid #b2d8a0;
          color: #2e6818;
          padding: 8px 12px;
          font-size: 12px;
          border-radius: 3px;
          margin-bottom: 12px;
        }
        .pma-alert-error {
          background-color: #fce4d6;
          border: 1px solid #f4b08a;
          color: #c00000;
          padding: 8px 12px;
          font-size: 12px;
          border-radius: 3px;
          margin-bottom: 12px;
          font-family: monospace;
        }
        .pma-results-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .pma-results-table th {
          background: #e8e8e8;
          border: 1px solid #ccc;
          padding: 6px 8px;
          text-align: left;
          font-weight: bold;
          color: #333;
        }
        .pma-results-table td {
          border: 1px solid #ddd;
          padding: 5px 8px;
          font-family: monospace;
        }
        .pma-results-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .pma-results-table tr:hover {
          background-color: #ffffcc;
        }
        .pma-null {
          color: #888;
          font-style: italic;
          background: #eee;
          padding: 1px 4px;
          border-radius: 2px;
        }
      `}</style>

      {/* Query Box */}
      <div className="pma-sql-box">
        <div className="pma-sql-legend">Run SQL query/queries on database:</div>

        <div className="pma-editor-wrapper">
          <AceEditor
            mode="sql"
            theme="github"
            onChange={(val) => setQuery(val)}
            name="pma-sql-editor"
            value={query}
            editorProps={{ $blockScrolling: true }}
            setOptions={{
              enableBasicAutocompletion: true,
              enableLiveAutocompletion: true,
              enableSnippets: true,
              wrap: true,
              useWorker: false,
              fontSize: '13px',
            }}
            width="100%"
            height="180px"
          />
        </div>

        <div className="pma-toolbar">
          <div className="pma-quick-buttons">
            <button
              type="button"
              className="pma-quick-btn"
              onClick={() => setTemplateQuery('SELECT')}
            >
              SELECT *
            </button>
            <button
              type="button"
              className="pma-quick-btn"
              onClick={() => setTemplateQuery('INSERT')}
            >
              INSERT
            </button>
            <button
              type="button"
              className="pma-quick-btn"
              onClick={() => setTemplateQuery('UPDATE')}
            >
              UPDATE
            </button>
            <button
              type="button"
              className="pma-quick-btn"
              onClick={() => setTemplateQuery('DELETE')}
            >
              DELETE
            </button>
          </div>

          <div className="d-flex gap-2">
            <button type="button" className="pma-btn" onClick={handleClear}>
              Clear
            </button>
            <button
              type="button"
              className="pma-btn pma-btn-primary"
              onClick={executeQuery}
              disabled={loading}
            >
              {loading ? `Executing... (${executionTime}s)` : 'Go'}
            </button>
          </div>
        </div>
      </div>

      {/* Execution Error Banner */}
      {error && (
        <div className="pma-alert-error">
          <strong># Error: </strong>
          <span>{error}</span>
        </div>
      )}

      {/* Affected Rows Banner */}
      {rowsAffected !== null && (
        <div className="pma-alert-success">
          Query executed successfully. {rowsAffected} row(s) affected. (Query
          took {executionTime} seconds)
        </div>
      )}

      {/* Select Results Display */}
      {result && Array.isArray(result) && result.length > 0 && (
        <div className="pma-results-container">
          <div className="pma-alert-success">
            Showing rows 0 - {result.length - 1} ({result.length} total, Query
            took {executionTime} seconds)
          </div>

          {!isNestedArray ? (
            <div className="table-responsive">
              <table className="pma-results-table">
                <thead>
                  <tr>
                    {Object.keys(result[0] || {}).map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {Object.values(row).map((val, colIndex) => (
                        <td key={colIndex}>
                          {val !== null && val !== undefined ? (
                            String(val)
                          ) : (
                            <span className="pma-null">NULL</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            result.map((resultSet, setIndex) => (
              <div key={setIndex} className="mb-4">
                <div className="fw-bold mb-2">Result Set #{setIndex + 1}</div>
                {Array.isArray(resultSet) && resultSet.length > 0 ? (
                  <div className="table-responsive">
                    <table className="pma-results-table">
                      <thead>
                        <tr>
                          {Object.keys(resultSet[0] || {}).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {resultSet.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {Object.values(row).map((val, colIndex) => (
                              <td key={colIndex}>
                                {val !== null && val !== undefined ? (
                                  String(val)
                                ) : (
                                  <span className="pma-null">NULL</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-muted small">
                    No rows returned for this set.
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Empty Result Set Notification */}
      {result &&
        Array.isArray(result) &&
        result.length === 0 &&
        rowsAffected === null && (
          <div className="pma-alert-success">
            MySQL returned an empty result set (i.e. zero rows). (Query took{' '}
            {executionTime} seconds)
          </div>
        )}
    </div>
  );
};

export default SqlEditor;
